import React from 'react'
import { Card, Radio, Typography, Space, Alert } from 'antd'
import type { RadioChangeEvent } from 'antd'
import { BiMapPin, BiLocationPlus, BiGlobe } from 'react-icons/bi'

export type LocationPreferenceValue = 'postcode' | 'radius' | 'all'

interface LocationPreferenceToggleProps {
  value: LocationPreferenceValue
  onChange: (method: LocationPreferenceValue) => void
  disabled?: boolean
  postcodeArea?: string
}

function LocationPreferenceToggle(props: LocationPreferenceToggleProps) {
  const {
    value,
    onChange,
    disabled = false,
    postcodeArea,
  } = props
  const handleChange = (e: RadioChangeEvent) => {
    onChange(e.target.value as LocationPreferenceValue)
  }

  return (
    <Card
      title={
        <Space>
          <BiLocationPlus />
          <Typography.Text strong>Alert Method</Typography.Text>
        </Space>
      }
      size="small"
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          Choose how you want to receive location-based incident alerts.
        </Typography.Paragraph>

        <Radio.Group
          value={value}
          onChange={handleChange}
          disabled={disabled}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Radio value="postcode" style={{ alignItems: 'flex-start' }}>
              <div style={{ marginLeft: 8 }}>
                <Typography.Text strong>Postal Code Area</Typography.Text>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  Receive alerts for incidents in your registered business postal code area
                  {postcodeArea && (
                    <span style={{ fontWeight: 'bold' }}> ({postcodeArea})</span>
                  )}
                </Typography.Text>
              </div>
            </Radio>

            <Radio value="radius" style={{ alignItems: 'flex-start' }}>
              <div style={{ marginLeft: 8 }}>
                <Typography.Text strong>Map Pin Radius</Typography.Text>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  Receive alerts for incidents within a customizable radius of your chosen map location
                </Typography.Text>
              </div>
            </Radio>

            <Radio value="all" style={{ alignItems: 'flex-start' }}>
              <div style={{ marginLeft: 8 }}>
                <Typography.Text strong>All Incidents</Typography.Text>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  Receive alerts for all incidents reported on the platform, regardless of location
                </Typography.Text>
              </div>
            </Radio>
          </Space>
        </Radio.Group>

        {value === 'postcode' && (
          <Alert
            message="Postal Code Alerts"
            description="You'll receive notifications for any incidents reported within the same postal code as your registered business address."
            type="info"
            showIcon
            icon={<BiMapPin />}
          />
        )}

        {value === 'radius' && (
          <Alert
            message="Radius-Based Alerts"
            description="You'll receive notifications for incidents within your specified radius from your chosen map location, regardless of postal code boundaries."
            type="info"
            showIcon
            icon={<BiLocationPlus />}
          />
        )}

        {value === 'all' && (
          <Alert
            message="All Incidents"
            description="You'll receive notifications for every incident reported on the platform. This is ideal for accommodations with properties across multiple regions or countries."
            type="info"
            showIcon
            icon={<BiGlobe />}
          />
        )}
      </Space>
    </Card>
  )
}

LocationPreferenceToggle.defaultProps = {
  disabled: false,
  postcodeArea: undefined,
}

export default LocationPreferenceToggle
